import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { 
  FaArrowLeft, FaPaperPlane, FaPaperclip, FaImage, FaVideo,
  FaCheckDouble, FaClock
} from 'react-icons/fa';

export default function RentalConversation() {
  const { conversationId } = useParams();
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [messageContent, setMessageContent] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const isInitialLoad = useRef(true);
  const previousMessageCount = useRef(0);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchConversation();
    fetchMessages();
    
    // Poll for new messages every 5 seconds
    const interval = setInterval(fetchMessages, 5000);
    return () => clearInterval(interval);
  }, [conversationId, user]);

  useEffect(() => {
    // Only scroll to bottom on initial load or when new messages are added
    if (isInitialLoad.current && messages.length > 0) {
      scrollToBottom();
      isInitialLoad.current = false;
      previousMessageCount.current = messages.length;
    } else if (messages.length > previousMessageCount.current) {
      // New message added
      scrollToBottom();
      previousMessageCount.current = messages.length;
    }
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversation = async () => {
    try {
      const response = await axios.get(`${API}/rentals/conversations/${conversationId}`);
      setConversation(response.data);
    } catch (error) {
      console.error('Error fetching conversation:', error);
      toast.error('Conversation non trouvée');
      navigate('/rentals/conversations');
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(
        `${API}/rentals/conversations/${conversationId}/messages?user_id=${user.id}`
      );
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (10 MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10 MB)');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/rentals/messages/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setAttachments([...attachments, response.data]);
      toast.success('Fichier téléchargé');
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Erreur lors du téléchargement');
    } finally {
      setUploading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    
    if (!messageContent.trim() && attachments.length === 0) {
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(
        `${API}/rentals/conversations/${conversationId}/messages?user_id=${user.id}`,
        {
          content: messageContent.trim(),
          attachments: attachments
        }
      );

      setMessages([...messages, response.data]);
      setMessageContent('');
      setAttachments([]);
      scrollToBottom();
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Erreur lors de l\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  const removeAttachment = (index) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-xl text-[#0A2540]">{t('loading')}</div>
      </div>
    );
  }

  if (!conversation) return null;

  const isOwner = user.id === conversation.owner_id;
  const otherPartyName = isOwner ? conversation.renter_name : conversation.owner_name;

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <Card className="mb-4">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/rentals/conversations')}
                  className="text-[#0A2540]"
                >
                  <FaArrowLeft />
                </Button>
                <div>
                  <CardTitle className="text-[#0A2540]">
                    {conversation.product_name}
                  </CardTitle>
                  <p className="text-sm text-gray-600">
                    Conversation avec {otherPartyName}
                  </p>
                </div>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Messages Container */}
        <Card className="mb-4">
          <CardContent className="p-0">
            <div className="h-[500px] overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="text-center text-gray-500 py-12">
                  Aucun message pour le moment. Commencez la conversation !
                </div>
              ) : (
                messages.map((message) => {
                  const isMyMessage = message.sender_id === user.id;
                  return (
                    <div
                      key={message.id}
                      className={`flex ${isMyMessage ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[70%] ${isMyMessage ? 'bg-[#00D4FF] text-white' : 'bg-white border'} rounded-lg p-4 shadow`}>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-sm">
                            {message.sender_name}
                          </span>
                          <span className={`text-xs ${isMyMessage ? 'text-white/70' : 'text-gray-500'}`}>
                            {new Date(message.created_at).toLocaleString('fr-FR', {
                              day: '2-digit',
                              month: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                        
                        <p className="whitespace-pre-wrap">{message.content}</p>

                        {/* Attachments */}
                        {message.attachments && message.attachments.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {message.attachments.map((att, idx) => (
                              <div key={idx}>
                                {att.type === 'image' ? (
                                  <img
                                    src={`${API}${att.url}`}
                                    alt={att.filename}
                                    className="max-w-full rounded cursor-pointer"
                                    onClick={() => window.open(`${API}${att.url}`, '_blank')}
                                  />
                                ) : (
                                  <video
                                    src={`${API}${att.url}`}
                                    controls
                                    className="max-w-full rounded"
                                  />
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Offered Price */}
                        {message.offered_price && (
                          <div className={`mt-3 pt-3 border-t ${isMyMessage ? 'border-white/20' : 'border-gray-200'}`}>
                            <p className="font-semibold">Prix proposé: {message.offered_price}€</p>
                            {message.offered_duration && (
                              <p className="text-sm">Durée: {message.offered_duration}</p>
                            )}
                          </div>
                        )}

                        {/* Read Status */}
                        {isMyMessage && (
                          <div className="flex justify-end mt-2">
                            {message.is_read ? (
                              <FaCheckDouble className="text-white/70" size={12} />
                            ) : (
                              <FaClock className="text-white/70" size={12} />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>
          </CardContent>
        </Card>

        {/* Message Input */}
        <Card>
          <CardContent className="p-4">
            {/* Attachment Preview */}
            {attachments.length > 0 && (
              <div className="mb-4 flex flex-wrap gap-2">
                {attachments.map((att, idx) => (
                  <div key={idx} className="relative">
                    <div className="flex items-center gap-2 bg-gray-100 p-2 rounded">
                      {att.type === 'image' ? <FaImage /> : <FaVideo />}
                      <span className="text-sm">{att.filename}</span>
                      <button
                        onClick={() => removeAttachment(idx)}
                        className="text-red-500 hover:text-red-700 ml-2"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <form onSubmit={handleSendMessage} className="flex gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept="image/*,video/*"
                className="hidden"
              />
              
              <Button
                type="button"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                <FaPaperclip />
              </Button>

              <Textarea
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                placeholder={t('rental.typeMessage')}
                className="flex-1"
                rows={2}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSendMessage(e);
                  }
                }}
              />

              <Button
                type="submit"
                disabled={sending || (!messageContent.trim() && attachments.length === 0)}
                className="bg-[#00D4FF] hover:bg-[#00B4D8]"
              >
                <FaPaperPlane />
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
